import type { StoredData } from '../../types/session';
import { storedDataSchema } from './sessionSchema';

export const STORAGE_KEY = 'instagram-fit-test:v1';

function detectLocalStorageAvailable(): boolean {
  try {
    const testKey = '__instagram_fit_test_probe__';
    window.localStorage.setItem(testKey, '1');
    window.localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
}

let memoryFallback: StoredData | undefined;
let localStorageAvailable: boolean | undefined;

/** Prüft einmalig, ob `localStorage` verfügbar ist (ERR-002). Ergebnis wird zwischengespeichert. */
export function isLocalStorageAvailable(): boolean {
  if (localStorageAvailable === undefined) {
    localStorageAvailable = typeof window !== 'undefined' && detectLocalStorageAvailable();
  }
  return localStorageAvailable;
}

/** Lädt persistierte Daten. Liefert `undefined`, wenn nichts gespeichert oder die Daten ungültig sind. */
export function loadStoredData(): StoredData | undefined {
  if (!isLocalStorageAvailable()) {
    return memoryFallback;
  }
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (!raw) return undefined;
  try {
    const parsed: unknown = JSON.parse(raw);
    const result = storedDataSchema.safeParse(parsed);
    if (!result.success) return undefined;
    return result.data as StoredData;
  } catch {
    return undefined;
  }
}

let writeTimeout: ReturnType<typeof setTimeout> | undefined;
const WRITE_DEBOUNCE_MS = 250;

/** Speichert Daten sofort oder mit maximal 250 ms Verzögerung (Abschnitt 28.3). */
export function saveStoredData(data: StoredData, immediate = false): void {
  if (!isLocalStorageAvailable()) {
    memoryFallback = data;
    return;
  }
  const write = (): void => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch {
      memoryFallback = data;
    }
  };
  if (immediate) {
    if (writeTimeout) clearTimeout(writeTimeout);
    write();
    return;
  }
  if (writeTimeout) clearTimeout(writeTimeout);
  writeTimeout = setTimeout(write, WRITE_DEBOUNCE_MS);
}

/** Löscht alle lokalen Testdaten (Abschnitt 28.6). */
export function clearStoredSession(
  keepTheme: boolean,
  currentTheme: StoredData['ui']['theme'],
): void {
  const cleared: StoredData = {
    schemaVersion: '1.0.0',
    session: undefined,
    ui: { theme: keepTheme ? currentTheme : 'system' },
  };
  if (!isLocalStorageAvailable()) {
    memoryFallback = cleared;
    return;
  }
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(cleared));
  } catch {
    memoryFallback = cleared;
  }
}
